// Copyright © 2025 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { Fragment, useEffect, useState, createContext } from 'react';
import { connect } from 'react-redux';
import compose from 'recompose/compose';
import { arrayOf, bool, func, number, object, shape, string } from 'prop-types';
import {
  Button,
  Grid,
  Link,
  withStyles,
  Tooltip,
  ExpansionPanel,
  ExpansionPanelSummary,
  ExpansionPanelDetails,
  Radio,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import WarningIcon from "@material-ui/icons/Warning";
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import AddIcon from '@material-ui/icons/Add';
import { useIntl } from 'react-intl';
import { cloneDeep, difference, findIndex, each, keys, uniq, map, get, lowerCase, isEmpty } from 'lodash';

import {
  FilterByName,
  FilterByTags,
  FilterByState,
  HeaderActionButtons,
  FilterBySeparator,
} from './controls';
import styles from './styles';
import {
  Switch,
  TagsGroup,
  FormDialog as Dialog,
  AlertDialog,
} from '../../../../components';
import ListContainer from '../../../list';
import {
  GRID_ROWS_PER_PAGE_OPTIONS,
  GRID_ROWS_PER_PAGE_DEFAULT_OPTION,
  ASSIGNED,
  NOT_ASSIGNED,
  APPLICATION_STATUS_INCOMPLETE,
  APPLICATION_STATUS_REJECTED,
 } from '../../../../constants';
import {
  fetchAllTags,
} from '../../../../actions/api';
import {
  fetchApis,
  updateApiAccess,
  updateApiPlan,
  resetApiUpdateFlag,
  fetchApiEula,
  fetchAvailableApiPlans,
} from '../../../../actions/application';
import {
  getApisListResults,
  getApisListTotalElements,
  getApisListTotalPages,
  getIsApiUpdateSuccess,
  getApiEula,
  getAvailableApiPlans,
} from '../../../../reducers/application';
import {
  getAllPortalTags,
} from '../../../../reducers/api';
import { getStatusColor, getStatusLabel } from "../../../Api/List/utils";
import { getI18n } from '../../../../utils/intl';
import ApplicationFooter from "../Footer";

export const AppAPIListContext = createContext();
const CHANGE_MODE_REMOVE_ACCESS = 'CHANGE_MODE_REMOVE_ACCESS';
const CHANGE_MODE_ADD_ACCESS = 'CHANGE_MODE_ADD_ACCESS';

export const Apis = (props) => {
  const {
    classes,
    fetchList,
    fetchAllTags,
    fetchApiEula,
    updateApiAccess,
    updateApiPlan,
    resetApiUpdateFlag,
    isApiUpdateSuccess,
    notifyMessages,
    rows = [],
    totalElements,
    totalPages,
    applicationDetails,
    allTags,
    isCurrentTab,
    hasPublisherUser,
    apiPlansEnabled,
    locked,
    isEditDisabled,
    requestSettings,
    publishResult,
    isLocking,
    setActiveTab,
    eulaDetails,
    fetchAvailableApiPlans,
    availableApiPlans,
    isAdmin,
    appUuid,
  } = props;

  const { getI18nMessage } = getI18n(useIntl());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(GRID_ROWS_PER_PAGE_DEFAULT_OPTION);
  const [selectedRows, setSelectedRows] = useState([]);
  const [filterByName, setFilterByName] = useState('');
  const [filterByTag, setFilterByTag] = useState([]);
  const [filterByState, setFilterByState] = useState('All');
  const [showFormActionBtns, setShowFormActionBtns] = useState(false);
  const [changeMode, setChangeMode] = useState(CHANGE_MODE_REMOVE_ACCESS);
  const [currentApisRows, setCurrentApisRows] = useState([]);
  const [hasAppPublish, setHasAppPublish] = useState(false);
  const [modifiedRecords, setModifiedRecords] = useState(0);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [showEulaDialog, setShowEulaDialog] = useState(false);
  const [publishAfterEulaAccept, setPublishAfterEulaAccept] = useState(false);
  const [showApiPlanDialog, setShowApiPlanDialog] = useState(false);
  const [eulaApiMap, setEulaApiMap] = useState();
  const [eulaDetailsMap, setEulaDetailsMap] = useState();
  const [expandedEulaPanel, setExpandedEulaPanel] = useState('first');
  const [apiPlansList, setApiPlansList] = useState([]);
  const [hasApiPlanChanged, setHasApiPlanChanged] = useState(false);
  const [isApiPlanSave, setIsApiPlanSave] = useState(true);
  const emptyFunction = () => {};

  useEffect(() => {
    if (applicationDetails && isCurrentTab) {
      setChangeMode(CHANGE_MODE_REMOVE_ACCESS);
      setSelectedRows([]);
      fetchListWithFilters();
      fetchAllTags();
    }
  }, [applicationDetails, isCurrentTab]);

  useEffect(() => {
    const plans = selectedRows.length > 0 && availableApiPlans
      ? availableApiPlans[selectedRows[0]] : [];
    if(plans && plans.length > 0 ){
      const apiRowObject = currentApisRows.find(item => item.apiUuid === selectedRows[0]);
      plans.map(item => {
        if(item.uuid === apiRowObject.apiPlanUuid) {
          item.isChecked = true;
        }
      });
    }
    setApiPlansList(plans);
    if(plans && plans.length > 1) {
      setShowApiPlanDialog(true);
    } else if (plans && plans.length === 1 && changeMode !== CHANGE_MODE_REMOVE_ACCESS) {
      const index = findIndex(currentApisRows, item => item.apiUuid === selectedRows[0]);
      currentApisRows[index].apiPlanName = plans[0].name;
      currentApisRows[index].apiPlanUuid = plans[0].uuid;
    }
  }, [availableApiPlans])

  useEffect(() => {
    if(eulaDetails && eulaDetails.uuid) {
      setEulaDetailsMap({
        ...eulaDetailsMap,
        [eulaDetails.uuid]: eulaDetails,
      })
    }
  },[eulaDetails]);

  const getListColumns = ({ getI18nMessage }) => [{
    id: 'name',
    label: getI18nMessage('label.api.list.column.name'),
    minWidth: 200,
    format: ({ name, apiUuid } = {}) => (<Link underline="none" href={`/publish/apis/details/${apiUuid}`}>
      {name}
    </Link>),
    value: ({ name, apiUuid } = {}) => ({ name, apiUuid }),
  },{
    id: 'tags',
    label: getI18nMessage('label.application.details.apis.tags.column.title'),
    minWidth: 300,
    value: (item) => (
      <div className={classes.tagsGroupContainer}>
        <TagsGroup
          onTagClick={(tag) => item.onTagClick(tag, item.tags)}
          tags={item.tags}
        />
      </div>
    ),
  },{
    id: 'portalStatus',
    label: getI18nMessage('label.state'),
    minWidth: 50,
    value: (item) => {
      const { portalStatus } = item;
      const status = getStatusLabel(portalStatus);
      const statusIconStyle = {
        fill: getStatusColor(portalStatus),
      };
      return (
        <span>
            <FiberManualRecordIcon
              size="0.82em" key={item.apiUuid} style={statusIconStyle} className={classes.statusIcon} />
            {status === 'Deprecated' || status === 'Disabled' ?
              <span> {status}
                <Tooltip
                  title={status === 'Deprecated' ?
                    getI18nMessage('label.application.details.deprecated.api.locked')
                  :
                    getI18nMessage('label.application.details.disabled.api.locked')}
                  arrow
                  placement="top"
                >
                  <WarningIcon className={classes.warningIcon} />
                </Tooltip>
              </span>
            :
            <span>{status}</span>
            }
        </span>);
    },
  },{
    id: 'appApiAccessStatus',
    label: getI18nMessage('label.api.details.apiproducts.list.access.title'),
    minWidth: 50,
    // eslint-disable-next-line react/prop-types
    format: (item) =>
      (<Switch
        id={`${item.apiUuid}-access`}
        onChange={(val) => onAccessToggle(val, item.apiUuid, item.apiEulaUuid, item)}
        checked={item.appApiAccessStatus === 'ASSIGNED'}
        disabled={locked || isEditDisabled}
      />
    ),
    value: (item) => (item),
  },{
    id: 'apiplanName',
    label: getI18nMessage('label.application.details.apis.apiplan.column.title'),
    minWidth: 100,
    format: (item) => {
      return (
        (locked || isEditDisabled) ?
        <span>{item.apiPlanName}</span> :
        <Link
          id={`api-plan-select-${item.apiPlanUuid}`}
          component="button"
          onClick={() => onApiPlanClick(item.apiUuid, true, item)}
        >
          {item.apiPlanName ? item.apiPlanName :
            <AddIcon size="0.82em" key={item.apiUuid} className={classes.statusIcon} />
          }
        </Link>
      )
    },
    value: (item) => (item),
    isHidden: !apiPlansEnabled,
  }];

  const getApiPlansListColumns = () => [{
    id: 'selected',
    label: '',
    minwidth: 30,
    maxWidth: 40,
    value: (item) =>
      <Radio id={item.uuid}
        name="api-plan-radio"
        checked={item.isChecked}
        onChange={() => onApiPlanChange(item.uuid, item.name)}
        className={classes.radioButton}
      />,
    },{
    id: 'name',
    label: getI18nMessage('label.application.details.apis.apiplans.dialog.columns.apiplan.title'),
    minwidth: 100,
    value: (item) => item.name,
  },{
    id: 'qrl',
    label: getI18nMessage('label.application.details.apis.apiplans.dialog.columns.qrl.title'),
    minwidth: 100,
    value: (item) => getQRLContent(item),
  }];

  const getQRLContent= item => {
    const limit = get(item, 'rateLimit') ? `${get(item, 'rateLimit')} / sec` : 'None';
    const quota = get(item, 'quota') ? `${get(item, 'quota')} / ${lowerCase(get(item, 'quotaInterval'))}`
    : 'None';
    return `Rate: ${limit}; Quota: ${quota}`;
  }

  const onApiPlanChange = (uuid) => {
    apiPlansList.map(item => {
      if(item.uuid === uuid) {
        setHasApiPlanChanged(true);
        item.isChecked = true;
      } else {
        setHasApiPlanChanged(false);
        item.isChecked = false;
      }
    });
    setIsApiPlanSave(false);
    setApiPlansList(apiPlansList);
  }
  const onApiPlanClick = (apiUuid, isIconClick) => {
    setIsApiPlanSave(true);
    setSelectedRows([apiUuid]);
    if(isIconClick) {
      setShowApiPlanDialog(true);
    }
    fetchAvailableApiPlans(applicationDetails.organizationUuid, apiUuid);
  };

  const onSubmitApiPlan = () => {
    const planObject = apiPlansList.find(item => item.isChecked === true);
    if(planObject && planObject.uuid) {
      const index = findIndex(currentApisRows, item => item.apiUuid ===
        selectedRows[0]);
        currentApisRows[index].apiPlanName = planObject.name;
        currentApisRows[index].apiPlanUuid = planObject.uuid;
        currentApisRows[index].hasApiPlanChanged = true;
      if(currentApisRows[index].appApiAccessStatus !== ASSIGNED) {
        onAccessToggle(true, currentApisRows[index].apiUuid,
          currentApisRows[index].apiEulaUuid, currentApisRows[index], true);
      }
      setCurrentApisRows([...currentApisRows]);
    }
    setIsApiPlanSave(false);
    setShowApiPlanDialog(false);
  }

  const onCancelApiPlan = () => {
    if(apiPlansList.length > 0) {
    const index = findIndex(currentApisRows, item => item.apiUuid ===
      selectedRows[0]);
    if(!currentApisRows[index].apiPlanUuid && changeMode !== CHANGE_MODE_REMOVE_ACCESS) {
      currentApisRows[index].appApiAccessStatus = NOT_ASSIGNED;
      setCurrentApisRows([...currentApisRows]);
      const eulaUuid = currentApisRows[index].apiEulaUuid;
      if(eulaUuid in eulaApiMap) {
        const currentEulaApis = eulaApiMap[eulaUuid].filter(api =>
              api.apiUuid !== selectedRows[0]);
        if(isEmpty(currentEulaApis)) {
          const tempMap = eulaApiMap;
          delete tempMap[eulaUuid];
          setEulaApiMap(tempMap);
        } else {
          setEulaApiMap({
            ...eulaApiMap,
            [eulaUuid]: currentEulaApis,
          });
        }
      }
    }
    setSelectedRows([]);
    }
    setShowApiPlanDialog(false);
  }

  useEffect(() => {
    if ((changeMode === CHANGE_MODE_ADD_ACCESS) || isFormDirty) {
      setShowFormActionBtns(true);
    }
    if ((changeMode !== CHANGE_MODE_ADD_ACCESS) && !isFormDirty && !hasAppPublish) {
      setShowFormActionBtns(false);
    }
  }, [changeMode, isFormDirty]);

  useEffect(() => {
    if(applicationDetails.status === APPLICATION_STATUS_INCOMPLETE ||
      applicationDetails.status === APPLICATION_STATUS_REJECTED) {
        setHasAppPublish(true);
        setShowFormActionBtns(true);
      } else {
        setHasAppPublish(false);
      }
  }, [applicationDetails])

  useEffect(() => {
    const rowsData = map(rows, (item) => ({ uuid: item.apiUuid, ...item, onTagClick }));
    setCurrentApisRows(cloneDeep(rowsData));
  }, [rows]);

  useEffect(() => {
    if(isCurrentTab) {
      if (isApiUpdateSuccess || publishResult && publishResult.result === 'SUCCESS') {
        props.fetchSingleApp(applicationDetails.uuid);
        const message = requestSettings &&
        requestSettings.editApplicationRequestWorkflowStatus === 'ENABLED' && !isAdmin && publishAfterEulaAccept ?
         getI18nMessage('label.application.details.section.org.accept.success')
        : getI18nMessage('label.application.update.success');
        notifyMessages(message, 'success');
        setChangeMode(CHANGE_MODE_REMOVE_ACCESS);
        setShowFormActionBtns(false);
        setPage(0);
        setSelectedRows([]);
        setEulaApiMap('');
        fetchListWithFilters({ hasAccess : true });
        resetApiUpdateFlag();
        setTimeout(() => {
          notifyMessages('', '');
        }, 2500);
        if(!isAdmin && requestSettings &&
          (applicationDetails.status !== APPLICATION_STATUS_INCOMPLETE ||
          publishResult && publishResult.result === 'SUCCESS') &&
          requestSettings.editApplicationRequestWorkflowStatus === 'ENABLED') {
          window.location.href=`/admin/console/applications/details/${appUuid}/requests`;
        }
      } else if(publishResult && publishResult.result === 'FAIL') {
        props.fetchSingleApp(applicationDetails.uuid);
        setChangeMode(CHANGE_MODE_REMOVE_ACCESS);
        setPage(0);
        setSelectedRows([]);
        setEulaApiMap('');
        fetchListWithFilters({ hasAccess : true });
        resetApiUpdateFlag();
        setTimeout(() => {
          notifyMessages('', '');
        }, 2500);
      }
      setPublishAfterEulaAccept(false);
    }
  }, [isApiUpdateSuccess, publishResult]);

  const onAccessToggle = (val, apiUuid, eulaUuid, apiObj, isApiPlanClick) => {
    if(val) {
      const eulaApiMapLocal = eulaApiMap || {};
      eulaApiMapLocal[eulaUuid] = eulaApiMap && eulaApiMapLocal[eulaUuid] ?
        [...eulaApiMapLocal[eulaUuid], apiObj] : [apiObj];
      setEulaApiMap(eulaApiMapLocal);
      if(apiPlansEnabled && !isApiPlanClick) {
        onApiPlanClick(apiUuid);
      }
    }
    // If the user deselects the selected api while in CHANGE_MODE_ADD_ACCESS mode,
    // then eulaApiMap needs to remove the eulaApiMap record for the deselected api.
    if(changeMode === CHANGE_MODE_ADD_ACCESS && !val) {
      if(eulaUuid in eulaApiMap) {
        setEulaApiMap((prevEulaApiMap) => {
          prevEulaApiMap[eulaUuid] = prevEulaApiMap[eulaUuid].filter(api =>
            api.apiUuid !== apiUuid);
          if(isEmpty(prevEulaApiMap[eulaUuid])) {
            delete prevEulaApiMap[eulaUuid];
          }
          return prevEulaApiMap;
        });
      }
    }
    updateCurrentApiRow(apiUuid, !!val, isApiPlanClick);
  };

  useEffect(() => {
    const rowsMap = {};
    each(rows, item => {
      rowsMap[item.apiUuid] = item;
    });
    const currentApisRowsMap = {};
    each(currentApisRows, item => {
      currentApisRowsMap[item.apiUuid] = item;
    });
    const rowIds = keys(rowsMap);
    const currentApisRowsIds = keys(currentApisRowsMap);
    if ( difference(rowIds, currentApisRowsIds).length !== 0) {
      return ;
    }
    let modifiedRecords = 0;
    each(rowIds, id => {
      if (rowsMap[id].appApiAccessStatus !== currentApisRowsMap[id].appApiAccessStatus ||
        rowsMap[id].apiPlanUuid !== currentApisRowsMap[id].apiPlanUuid) {
        modifiedRecords++;
      }
    });
    setModifiedRecords(modifiedRecords);
  }, [currentApisRows]);

  useEffect(() => {
    if (modifiedRecords > 0) {
      setIsFormDirty(true);
    } else {
      setIsFormDirty(false);
    }
  }, [modifiedRecords]);



  useEffect(() => {
    if(isCurrentTab) {
      fetchListWithFilters();
    }
  }, [changeMode]);

  const updateCurrentApiRow = (uuid, access, isApiPlanClick) => {
    const index = findIndex(currentApisRows, item => item.apiUuid === uuid);
    currentApisRows[index].appApiAccessStatus = access ? ASSIGNED : NOT_ASSIGNED;

    //while adding reset the apiplan object
    if(access && apiPlansEnabled && !isApiPlanClick &&
      changeMode === CHANGE_MODE_REMOVE_ACCESS) {
        currentApisRows[index] = Object.assign({}, rows[index]);
        currentApisRows[index].appApiAccessStatus = ASSIGNED;
    }
  //while removing reset the apiplan object
    if(!access && apiPlansEnabled && !isApiPlanClick) {
      if(changeMode === CHANGE_MODE_ADD_ACCESS) {
        currentApisRows[index] = Object.assign({}, rows[index]);
      } else {
        if(currentApisRows[index].apiPlanUuid) {
          currentApisRows[index].apiPlanUuid = null;
          currentApisRows[index].apiPlanName = '';
        }
      }
      currentApisRows[index].appApiAccessStatus = NOT_ASSIGNED;
    }
    setCurrentApisRows([...currentApisRows]);
  };

  const fetchListWithFilters = ({
    hasAccess,
    name = filterByName,
    pageNum = page,
    rowsPerPageArg = rowsPerPage,
    tags = filterByTag,
    state = filterByState,
  } = { }) => {
    const access = hasAccess || changeMode === CHANGE_MODE_REMOVE_ACCESS ? true : false;
    fetchList({
      appUuid: appUuid,
      filterByName : name,
      hasAccess: access,
      page: pageNum,
      rowsPerPage: rowsPerPageArg,
      portalStatus: state === 'All' ? 'ENABLED,DEPRECATED,DISABLED' : state,
      tags,
    });
  };
  // tags related code
  const allTagsOptions = allTags.map(({ name }) => ({ value: name, label: name }));
  const onTagsSelectionChange = (items) => {
    onTagsSelect(uniq(map(items, item => item.value)));
  };

  const onTagsSelect = (tags) => {
    setPage(0);
    setFilterByTag(uniq(tags));
    fetchListWithFilters({ tags: tags, pageNum: 0 });
  };
  const onTagClick = (tag, apiTags) => {
    onTagsSelect(uniq([...filterByTag, tag]), apiTags);
  };
  const onFilterByNameKeyPress = (e) => {
    if (e.key === 'Enter') {
      setPage(0);
      fetchListWithFilters({ pageNum: 0 });
    }
  };
  const onFilterByStateChange = (value) => {
    setFilterByState(value);
    setPage(0);
    fetchListWithFilters({ state: value, pageNum: 0 });
  };

  const onChangePage = (newPage) => {
    if (page === newPage) { return; }
    setPage(newPage);
    fetchListWithFilters({ pageNum: newPage });
  };
  const onChangePreviousPage = () => { onChangePage(page - 1); };
  const onChangeNextPage = () => { onChangePage(page + 1); };

  const onChangeRowsPerPage = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchListWithFilters({ pageNum: 0, rowsPerPageArg : newRowsPerPage });
  };

  const addButtonLabel = getI18nMessage('label.api.add');
  const handleAddButton = () => {
    clearFilter();
    setChangeMode(CHANGE_MODE_ADD_ACCESS);
  };
  const showAddBtn = changeMode === CHANGE_MODE_REMOVE_ACCESS;
  const addBtnFunc = showAddBtn ? handleAddButton : emptyFunction;

  const handleExpandEulaPanel = eulaUuid => (event, expanded) =>
    setExpandedEulaPanel(expanded ? eulaUuid : false);

  const clearFilter = () => {
    setFilterByName('');
    setPage(0);
    setFilterByState('All');
    setFilterByTag([]);
  }

  const hasSelectedRowAvailable = (access) => {
    let hasIndex = false;
    selectedRows.forEach((uuid) => {
      const index = findIndex(
        currentApisRows, item => item.apiUuid === uuid && item.appApiAccessStatus === access);
      if(index > -1) {
        hasIndex = true;
      }
    });
    return hasIndex;
  }
  const isDisableAddAccess = () =>
    changeMode === CHANGE_MODE_REMOVE_ACCESS && !hasSelectedRowAvailable(NOT_ASSIGNED);
  const isDisableRemoveAccess = () =>
    changeMode === CHANGE_MODE_ADD_ACCESS && !hasSelectedRowAvailable(ASSIGNED);
  const onBulkUpdate = (access) => {
      const eulaApiMapLocal={};
      selectedRows.forEach((uuid) => {
        const index = findIndex(currentApisRows, item => item.apiUuid === uuid);
        currentApisRows[index].appApiAccessStatus = access;
        if(access === ASSIGNED) {
          const eulaUuid = currentApisRows[index].apiEulaUuid;
          eulaApiMapLocal[eulaUuid] = eulaApiMapLocal[eulaUuid] ?
           [...eulaApiMapLocal[eulaUuid], currentApisRows[index]] : [currentApisRows[index]];
        }
      });
      if(access === ASSIGNED) {
        setEulaApiMap(eulaApiMapLocal);
        setCurrentApisRows([...currentApisRows]);
      } else {
        setCurrentApisRows([...currentApisRows]);
      }
  };

  const onCancel = () => {
    clearFilter();
    if ((changeMode === CHANGE_MODE_ADD_ACCESS) && !isFormDirty) {
      setChangeMode(CHANGE_MODE_REMOVE_ACCESS);
    } else if ((changeMode === CHANGE_MODE_ADD_ACCESS) && isFormDirty) {
       fetchListWithFilters({ pageNum: 0 });
    }
    else if(changeMode === CHANGE_MODE_REMOVE_ACCESS && isFormDirty) {
      fetchListWithFilters({ pageNum: 0 });
    } else {
      setActiveTab('app-overview-tab');
    }
    setEulaApiMap('');
    setSelectedRows([]);
  }

  const closeEulaDialog = () => setShowEulaDialog(false);

  const onCancelEulaDialog = () => {
    if (changeMode === CHANGE_MODE_ADD_ACCESS) {
      fetchListWithFilters({ pageNum: 0 });
    } else {
      fetchListWithFilters({ pageNum: 0 });
    }
    setSelectedRows([]);
    setEulaApiMap('');
    setShowEulaDialog(false);
  }

  const saveUpdatedRows = ({ doPublish }) => {
    clearFilter();
    if(doPublish && !isFormDirty) {
      setPublishAfterEulaAccept(true);
      props.publishApplication(applicationDetails.uuid)
    }
    if (changeMode === CHANGE_MODE_REMOVE_ACCESS && !apiPlansEnabled) {
      const removedApis =
      currentApisRows.filter(item => item.appApiAccessStatus === NOT_ASSIGNED);
      if(removedApis.length > 0 ) {
        const data = map(removedApis,
            item => ({ uuid: item.apiUuid }),
          );
        updateApiAccess({
          appUuid: applicationDetails.uuid,
          action: 'remove',
          data,
          doPublish,
        });
      }
    } else if(changeMode === CHANGE_MODE_REMOVE_ACCESS && apiPlansEnabled) {
      const removedApis =
        currentApisRows.filter(item => item.appApiAccessStatus === NOT_ASSIGNED);
      const addApiPlans =
        currentApisRows.filter(item => item.appApiAccessStatus !== NOT_ASSIGNED && get(item, 'hasApiPlanChanged'));
      if(removedApis.length > 0 ) {
        const data = map(removedApis,
            item => ({ uuid: item.apiUuid, apiPlanUuid: (item.apiPlanUuid || null) }),
        );
        updateApiPlan({
          appUuid: applicationDetails.uuid,
          action: 'remove',
          data,
          doPublish,
        });
      }
      if(addApiPlans.length > 0) {
        const data = map(addApiPlans,
          item => ({ uuid: item.apiUuid, apiPlanUuid: item.apiPlanUuid ? item.apiPlanUuid : null }),
        );
        updateApiPlan({
          appUuid: applicationDetails.uuid,
          data,
          doPublish,
        });
      }
    } else if (changeMode === CHANGE_MODE_ADD_ACCESS) {
      if(!isEmpty(eulaApiMap)) {
        Object.keys(eulaApiMap).forEach(eulaUuid => fetchApiEula(eulaUuid));
        setShowEulaDialog(true);
      }
      if (doPublish) {
        setPublishAfterEulaAccept(true);
      }
    }
  };

  const onAddSave = () => {
    closeEulaDialog();
    if (changeMode === CHANGE_MODE_ADD_ACCESS) {
      const addedApis =
      currentApisRows.filter(item => item.appApiAccessStatus === ASSIGNED);
      if(addedApis.length > 0 && !apiPlansEnabled ) {
        const data = map(addedApis,
            item => ({ uuid: item.apiUuid }),
          );
        updateApiAccess({
          appUuid: applicationDetails.uuid,
          data,
          doPublish: publishAfterEulaAccept,
        });
      } else if(addedApis.length > 0 && apiPlansEnabled ) {
        const data = map(addedApis,
          item => ({ uuid: item.apiUuid, apiPlanUuid: item.apiPlanUuid ? item.apiPlanUuid : null }),
        );
        updateApiPlan({
          appUuid: applicationDetails.uuid,
          data,
          doPublish: publishAfterEulaAccept,
        });
      }
    }
  }


  const publishAssistText = <span>
    {getI18nMessage('label.application.details.footer.publish.assist.text1')}
    <br/>
    {getI18nMessage('label.application.details.footer.publish.assist.text2')}
  </span>;
  const modifiedText = getI18nMessage('label.application.details.apiproducts.list.save.assist.text', { num: modifiedRecords });
  const assistText = hasAppPublish && !isFormDirty ? publishAssistText :
    hasAppPublish && isFormDirty ? (<span>{modifiedText} {publishAssistText}</span>) :
    isFormDirty ? (<span>
    {modifiedText}
    <WarningIcon className={classes.warningIcon} />
    {getI18nMessage('label.redepolyment.required')}
    </span>) : '';

const saveText=getI18nMessage(`label.application.details.footer.${isLocking ? 'submit': 'save'}.button`);
const publishText=getI18nMessage(`label.application.details.footer.publish.${!hasPublisherUser
  && requestSettings && requestSettings.applicationRequestWorkflowStatus === 'ENABLED'? 'submit.': ''}button`);

const nextText = isFormDirty &&
  hasAppPublish ? `${saveText} & ${publishText}` : hasAppPublish ? publishText : saveText;

const ApiPlanSelect = () => (
  <ListContainer
    listPageId="app-api-apiplan-dialog"
    columns={getApiPlansListColumns()}
    rows={apiPlansList}
    noResultsMessage={getI18nMessage('label.application.details.apis.apiplans.no.results')}
    pageBodyClass={classes.pageBodyClass}
    pageClass={classes.pageClass}
    hasPaginationDisabled={true}
    hidePagination
    hideFooter
  />
);

const renderApiEulaDialog = () => (
  <Fragment>
    {eulaApiMap &&
      eulaDetailsMap &&
      Object.keys(eulaApiMap).map((apiEula, index) => ( eulaDetailsMap[apiEula] &&
      <ExpansionPanel
        expanded={expandedEulaPanel === apiEula || (index === 0 && expandedEulaPanel === 'first')}
        className={classes.apiGroupPanel}
        key={apiEula}
        onChange={handleExpandEulaPanel(apiEula)}
        >
        <ExpansionPanelSummary
          aria-controls="panel1a-content"
          expandIcon={<ExpandMoreIcon />}
          id="panel1a-header"
        >
          <span className={classes.heading}>
            <span className={classes.accordionTitleBold}>
              {getI18nMessage('label.application.details.apis.eulas.accordion.title',
            { index: index+1, count: Object.keys(eulaApiMap).length }) }</span>
            <span className={classes.accordionTitle}>{eulaApiMap[apiEula] && eulaApiMap[apiEula].map(api => api.name).join(', ')}</span>
          </span>
        </ExpansionPanelSummary>
        <ExpansionPanelDetails>
          <span
            className={classes.panelContent}
            dangerouslySetInnerHTML={{ __html: (eulaDetailsMap[apiEula].content) }}
          />
        </ExpansionPanelDetails>
      </ExpansionPanel>
      ))}
  </Fragment>
);

  return (
    <AppAPIListContext.Provider >
      <Dialog
         isOpen={showEulaDialog}
         title={getI18nMessage("label.application.details.apis.eulas.title")}
         component={renderApiEulaDialog}
         submitText={getI18nMessage("label.application.details.apis.eulas.acceptbutton")}
         cancelText={getI18nMessage("label.cancel.button")}
         onClose={closeEulaDialog}
         onSubmit={onAddSave}
         onCancel={onCancelEulaDialog}
         paperClass={classes.paperClass}
         handleClose={() => closeEulaDialog}
      >
        {renderApiEulaDialog()}
      </Dialog>
      {showApiPlanDialog &&
        <AlertDialog
          id="app-apis-apiplan-dialog"
          isOpen={showApiPlanDialog}
          title={getI18nMessage('label.rate.quota.assign.title')}
          component={<ApiPlanSelect hasApiPlanChanged={hasApiPlanChanged}/>}
          submitText={getI18nMessage('label.save.button')}
          cancelText={getI18nMessage('label.cancel.button')}
          showDefault={true}
          onClose={()=>(setShowApiPlanDialog(false))}
          onSubmit={onSubmitApiPlan}
          onCancel={onCancelApiPlan}
          containerClass={classes.dialogContainer}
          submitButtonClass={classes.submitButton}
          dialogActionClass={classes.dialogActionClass}
          showSubmit={apiPlansList.length > 0}
          disableSubmit={isApiPlanSave}
        />
      }
      <ListContainer
        hasPaginationDisabled={isFormDirty || selectedRows.length > 0}
        listPageId="app-apis-list"
        isLoading={false}
        notificationId="request-notifications"
        notificationStatus={''}
        setNotificationStatus={() => {}}
        notificationMessage={''}
        setNotificationMessage={() => {}}
        pageHeaderTitle={''}
        pageHeaderDescription={''}
        pageHeaderTooltipTitle={getI18nMessage('label.request.list.page.title.help')}
        addButtonLabel={''}
        onAdd={false}
        showListHeader={true}
        filterAndSortContent={(
          <Fragment>
            <FilterByName
              fieldContainerClass={classes.fieldContainer}
              handleChange={setFilterByName}
              name={getI18nMessage('label.filter')}
              onKeyPress={onFilterByNameKeyPress}
              placeholder={getI18nMessage('label.api.name')}
              value={filterByName}
            />
            <FilterByTags
              fieldContainerClass={classes.tagsContainer}
              name="tags"
              onChange={onTagsSelectionChange}
              noOptionsMessage={() => getI18nMessage('label.application.api.tag.nooptions.text')}
              optionsData={allTagsOptions}
              placeholder={getI18nMessage('label.application.api.tag.placeholder.text')}
              value={filterByTag}
            />
            {changeMode === CHANGE_MODE_REMOVE_ACCESS &&
              <FilterByState
                fieldContainerClass={classes.fieldContainer}
                name={getI18nMessage('label.filter')}
                selectFieldClass={classes.selectField}
                value={filterByState}
                handleChange={onFilterByStateChange}
                hideLabel
              />
            }
            {changeMode === CHANGE_MODE_ADD_ACCESS &&
              <FilterBySeparator />
            }
            <Grid item md={3} sm={4} xs={6}>
              {showAddBtn && <Button
                className={classes.addBtn}
                data-apim-test={'app-apis-add'}
                data-layer7-test={'app-apis-add'}
                disabled={isFormDirty || locked || isEditDisabled}
                id={'app-apis-add'}
                onClick={addBtnFunc}
                variant="contained" color="secondary"
              >
                {addButtonLabel}
              </Button>}
            </Grid>
          </Fragment>
        )}
        columns={getListColumns({ getI18nMessage, classes })}
        rows={currentApisRows}
        noResultsMessage={getI18nMessage('label.no.results')}
        page={page}
        totalElements={totalElements}
        totalPages={totalPages}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={GRID_ROWS_PER_PAGE_OPTIONS}
        onChangeRowsPerPage={onChangeRowsPerPage}
        onChangePage={onChangePage}
        onChangePreviousPage={onChangePreviousPage}
        onChangeNextPage={onChangeNextPage}
        notifyMessages={emptyFunction}
        pageClass={classes.listContent}
        pageBodyClass={classes.pageBodyClass}
        pageFilterAndSortClass={classes.pageFilterAndSortClass}
        setSelectedRows={setSelectedRows}
        selectedRows={selectedRows}
        hasBulkActions={false}
        bulkActionsContent={!apiPlansEnabled && !locked &&
          (<HeaderActionButtons
            {...props}
            id={'app-apis-header'}
            fieldContainerClass={classes.fieldContainer}
            bulkActionsItemsCount={selectedRows.length}
            classes={classes}
            onAssign={() => onBulkUpdate(ASSIGNED)}
            onUnassign={() => onBulkUpdate(NOT_ASSIGNED)}
            disableAdd={isDisableAddAccess()}
            disableRemove={isDisableRemoveAccess()}
          />)
        }
      />
      {showFormActionBtns &&
        <ApplicationFooter
          className={classes.appFooter}
          isSaveDisabled={!isFormDirty}
          onSave={() => saveUpdatedRows({ doPublish:false })}
          onPublish={() => saveUpdatedRows({ doPublish:true })}
          onCancel={onCancel}
          showPublish={applicationDetails.status === APPLICATION_STATUS_INCOMPLETE ||
            applicationDetails.status === APPLICATION_STATUS_REJECTED}
          saveText={getI18nMessage(`label.application.details.footer.${isLocking ? 'submit': 'save'}.button`)}
          publishText={nextText}
          assistText={assistText}
        />
      }
    </AppAPIListContext.Provider>
  );
};

const mapStateToProps = (state) => ({
  rows: getApisListResults(state),
  totalElements: getApisListTotalElements(state),
  totalPages: getApisListTotalPages(state),
  isApiUpdateSuccess: getIsApiUpdateSuccess(state),
  allTags: getAllPortalTags(state),
  eulaDetails: getApiEula(state),
  availableApiPlans: getAvailableApiPlans(state),
});

Apis.propTypes = {
  apiUuid: string,
  apiDetails: object,
  classes: shape({}),
  fetchList: func,
  isApiUpdateSuccess: bool,
  notifyMessages: func,
  rows: arrayOf(shape({})),
  totalElements: number,
  totalPages: number,
  updateApiAccess: func,
  updateApiPlan: func,
  userContext: object,
  applicationDetails: shape({}),
  isCurrentTab: bool,
  fetchAllTags: func,
  resetApiUpdateFlag: func,
  allTags: arrayOf(shape({})),
  hasPublisherUser: bool,
  apiPlansEnabled: bool,
  locked: bool,
  requestSettings: shape({}),
  publishResult: shape({}),
  isLocking: bool,
  publishApplication: func,
  setActiveTab: func,
  eulaDetails: shape({}),
  fetchApiEula: func,
  fetchAvailableApiPlans: func,
  availableApiPlans: arrayOf(shape({})),
  fetchSingleApp: func,
  isEditDisabled: bool,
  isAdmin: bool,
  setCurrentTab: func,
  appUuid: string,
};
Apis.displayName = 'Apis';

const mapDispatchToProps = {
  fetchList: fetchApis,
  fetchAllTags,
  updateApiAccess,
  updateApiPlan,
  resetApiUpdateFlag,
  fetchApiEula,
  fetchAvailableApiPlans,
};

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(Apis);
